import { useEffect, useState, type CSSProperties } from 'react';
import { erpApi } from '../../api/erp';
import type { DownloadAnalytics } from '../../api/download-analytics';
import { Button } from '../../components/ui/Button';
import { Card, StatCard } from '../../components/ui/Card';
import { formatCount } from '../../utils/numbers';
import './erp.css';
import './erp-downloads.css';

const products: Record<string, string> = { all: 'Todos los productos', tpv: 'TPV Hosty', remote: 'Hosty Remote', manual: 'Manuales PDF', other: 'Otros archivos' };
const sources = { web: 'Web de Hosty', updater: 'Actualizador TPV', direct: 'Directo / sin identificar' };
const dateLabel = (day: string) => new Intl.DateTimeFormat('es-ES', { day: 'numeric', month: 'short', timeZone: 'UTC' }).format(new Date(`${day}T00:00:00Z`));

export function ErpDownloadsPage() {
  const [days, setDays] = useState(30);
  const [product, setProduct] = useState('all');
  const [refresh, setRefresh] = useState(0);
  const [data, setData] = useState<DownloadAnalytics | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    setData(null);
    erpApi.getDownloads(days, product)
      .then(result => { if (!cancelled) setData(result); })
      .catch(err => { if (!cancelled) setError(err instanceof Error ? err.message : 'No se pudieron cargar las descargas.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [days, product, refresh]);

  const peak = Math.max(1, ...(data?.daily.map(day => day.completed) ?? []));
  return (
    <div className="erp-downloads">
      <header className="erp-page-heading">
        <div><h1>Descargas</h1><p className="muted">Consulta qué archivos se descargan y desde dónde.</p></div>
        <Button variant="secondary" disabled={loading} onClick={() => setRefresh(value => value + 1)}>Actualizar datos</Button>
      </header>

      <div className="erp-downloads__filters">
        <label htmlFor="download-period">Periodo
          <select id="download-period" className="erp-filter-input" value={days} onChange={event => setDays(Number(event.target.value))}>
            <option value={7}>Últimos 7 días</option><option value={30}>Últimos 30 días</option><option value={90}>Últimos 90 días</option>
          </select>
        </label>
        <label htmlFor="download-product">Producto
          <select id="download-product" className="erp-filter-input" value={product} onChange={event => setProduct(event.target.value)}>
            {Object.entries(products).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
        </label>
        <span className="muted erp-downloads__timezone">Días naturales · UTC</span>
      </div>

      <div aria-live="polite" aria-atomic="true">
        {loading && <p role="status">Cargando descargas…</p>}
        {error && <p className="ui-alert ui-alert--error" role="alert">{error} Puedes volver a intentarlo con «Actualizar datos».</p>}
      </div>
      {data && <>
        <div className="erp-stats">
          <StatCard label="Descargas completas" value={formatCount(data.totals.completed)} hint="Archivo completo servido por Hosty" />
          <StatCard label="Transferencias parciales" value={formatCount(data.totals.partial)} hint="Fragmentos o reanudaciones · no se suman al total" />
          <StatCard label="Transferencias interrumpidas" value={formatCount(data.totals.aborted)} hint="Conexión cerrada antes de terminar el envío" />
        </div>

        {!data.artifacts.length ? <section className="erp-empty-state" role="status">
          <h2>Aún no hay descargas en este periodo</h2>
          <p className="muted">Los datos aparecerán cuando se descargue un archivo de Hosty. No se recuperan descargas anteriores al registro.</p>
        </section> : <>
          <div className="erp-downloads__overview">
            <Card title="Evolución diaria" subtitle="Descargas completas en el periodo seleccionado">
              <div className="erp-downloads__chart" aria-hidden="true">
                {data.daily.map(day => <div key={day.day} className="erp-downloads__bar-track" title={`${dateLabel(day.day)}: ${formatCount(day.completed)}`}>
                  <div className="erp-downloads__bar" style={{ '--bar-height': `${day.completed / peak * 100}%` } as CSSProperties} />
                </div>)}
              </div>
              <div className="erp-downloads__axis" aria-hidden="true"><span>{dateLabel(data.daily[0].day)}</span><span>{dateLabel(data.daily[data.daily.length - 1].day)}</span></div>
              <details className="erp-downloads__daily">
                <summary>Ver cifras por día</summary>
                <div className="erp-table-wrap"><table className="erp-table">
                  <caption className="sr-only">Descargas completas por día, en UTC</caption>
                  <thead><tr><th scope="col">Día (UTC)</th><th scope="col">Completas</th></tr></thead>
                  <tbody>{data.daily.map(day => <tr key={day.day}><th scope="row">{dateLabel(day.day)}</th><td>{formatCount(day.completed)}</td></tr>)}</tbody>
                </table></div>
              </details>
            </Card>
            <Card title="Origen de las descargas" subtitle="Solo archivos completos">
              <dl className="erp-downloads__sources">
                {Object.entries(sources).map(([key, label]) => <div key={key}><dt>{label}</dt><dd>{formatCount(data.sources[key as keyof typeof sources])}</dd></div>)}
              </dl>
              <p className="muted erp-downloads__hint">El origen se identifica por el enlace utilizado; no representa usuarios únicos.</p>
            </Card>
          </div>
          <section aria-labelledby="download-files-title">
            <h2 id="download-files-title" className="erp-downloads__section-title">Por archivo y versión</h2>
            <div className="erp-table-wrap" role="region" aria-label="Desglose de descargas por archivo" tabIndex={0}>
              <table className="erp-table erp-downloads__files">
                <caption className="sr-only">Archivos servidos en el periodo seleccionado</caption>
                <thead><tr><th scope="col">Archivo</th><th scope="col">Versión</th><th scope="col">Completas</th><th scope="col">Parciales</th><th scope="col">Interrumpidas</th></tr></thead>
                <tbody>{data.artifacts.map(file => <tr key={file.artifact}>
                  <th scope="row"><strong>{products[file.product] ?? 'Otros archivos'}</strong><span className="erp-table-secondary">{file.artifact}</span></th>
                  <td>{file.version ? `v${file.version}` : 'Sin versión'}</td><td>{formatCount(file.completed)}</td><td>{formatCount(file.partial)}</td><td>{formatCount(file.aborted)}</td>
                </tr>)}</tbody>
              </table>
            </div>
          </section>
        </>}
      </>}
      <p className="muted erp-downloads__footnote">Medimos respuestas del servidor, no instalaciones ni usuarios únicos. Los reintentos completos cuentan de nuevo; los fragmentos no se unen. No incluye App Store, Google Play ni OTA. Sin cookies de seguimiento ni datos personales.</p>
    </div>
  );
}
